import { useEffect, useRef } from 'react';

interface EmojiMatch {
  shortcode: string;
  emoji: string;
}

interface EmojiAutocompleteProps {
  matches: EmojiMatch[];
  selectedIndex: number;
  onSelect: (emoji: string, shortcode: string) => void;
  position: { top: number; left: number };
}

/**
 * Emoji autocomplete dropdown for :shortcode completion
 * - Shows matching emojis as user types
 * - Keyboard navigation support
 * - Mobile-friendly touch targets
 */
export function EmojiAutocomplete({
  matches,
  selectedIndex,
  onSelect,
  position,
}: EmojiAutocompleteProps) {
  const listRef = useRef<HTMLDivElement>(null);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current && selectedIndex >= 0) {
      const selectedElement = listRef.current.children[selectedIndex] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  if (matches.length === 0) return null;

  return (
    <div
      ref={listRef}
      className="absolute z-50 bg-slate-800 border border-slate-700 rounded-lg shadow-xl overflow-y-auto max-h-48 w-64"
      style={{
        bottom: `${position.top}px`,
        left: `${position.left}px`,
      }}
      data-testid="emoji-autocomplete"
    >
      {matches.map((match, index) => (
        <button
          key={match.shortcode}
          type="button"
          onClick={() => onSelect(match.emoji, match.shortcode)}
          className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors touch-manipulation min-h-[44px] ${
            index === selectedIndex ? 'bg-blue-600 text-white' : 'hover:bg-slate-700 text-slate-100'
          }`}
          data-testid={`emoji-autocomplete-item-${match.shortcode}`}
        >
          <span className="text-xl">{match.emoji}</span>
          <span className="text-sm font-mono">:{match.shortcode}:</span>
        </button>
      ))}
    </div>
  );
}
